/**
 * useBootstrap — runs once when the session transitions to non-null.
 * Calls POST /api/me/bootstrap to ensure the server-side user/org/subscription
 * exist. Seeds the active-org id afterwards. Safe on every app mount
 * (bootstrap itself is idempotent).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface BootstrapResponse {
  created: boolean;
  org_id: string;
  org_slug: string;
  org_name: string;
  tier: string;
}

interface MeOrg {
  id: string;
  name: string;
  slug: string;
  role: string;
  tier: string;
  founding_customer: boolean;
}

interface MeResponse {
  user: { id: string; email: string; full_name: string | null; is_admin: boolean };
  organizations: MeOrg[];
  active_org_id: string;
}

export function useBootstrap() {
  const { session, activeOrgId, setActiveOrgId } = useAuth();

  const bootstrap = useMutation({
    mutationFn: () => api<BootstrapResponse>('/api/me/bootstrap', { method: 'POST' }),
    onSuccess: (data) => {
      if (!activeOrgId) setActiveOrgId(data.org_id);
    },
  });

  useEffect(() => {
    if (session && !bootstrap.isPending && !bootstrap.data) {
      bootstrap.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  return bootstrap;
}

export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/me'),
    enabled,
  });
}
